import React, { useCallback } from 'react';
import { MdClose } from 'react-icons/md';
import { FileDrop, Modal } from '~/components';
import { FormGroup, FormSelect, TextArea } from '~/components/form';
import { useForm } from '~/hooks';
import { DocumentType } from '~/api/document';

const deedState = {
    sectionId: 0,
    sectionDetailId: 0,
    titleOpinionId: 0,
    mortgators: '',
    beneficiary: '',
    trustee: '',
    securingAmountOf: '',
    purchasePrice: null,
    dated: null,
    recordedOn: null,
    book: '',
    page: '',
    countyRegistry: '',
    reRecordedOn: null,
    reBook: '',
    rePage: '',
    reCountyRegistry: '',
    assignedTo: '',
    assignedBook: '',
    assignedPage: '',
    assignedCountyRegistry: '',
    lienOrder: null,
    lienOrderOptions: [
        { label: 'First', value: 1 },
        { label: 'Second', value: 2 },
        { label: 'Third', value: 3 },
        { label: 'Fourth', value: 4 }
    ],
    copyOfDeed: []
};

const DeedOfTrustForm = ({ isOpen, edit = false, readonly = false, deed = {}, saveDeed, closeModal }) => {

    const onLoad = useCallback(async (values, props) => {
        if (props.edit) {
            const { deed } = props;
            let copyOfDeed = [];

            if (Array.isArray(deed.documents)) {
                copyOfDeed = deed.documents.map(d => {
                    return { 
                        file: { 
                            path: d.name, 
                        }, 
                        type: d.typeId, 
                        uploaded: true, 
                        isDeleted: false,
                        ...d
                    };
                });
            }

            return { 
                ...values,
                sectionId: props.deed.sectionId,
                sectionDetailId: props.deed.sectionDetailId,
                ...props.deed._data, 
                copyOfDeed };
        } 
        
        return { ...values };
    }, [isOpen, edit]);

    const {
        state,
        fieldActions,
        formActions
    } = useForm({
        initialState: deedState,
        onLoad,
        props: { edit, deed }
    });

    return (
        <Modal isModalOpen={isOpen} preventScroll className='w-3/4 h-5/6 bg-clip-border bg-white rounded-md'>
            <section className='h-full flex flex-col'>
                <header className='pt-3 pb-2 px-8 border-b flex items-center justify-between'>
                    <p className='font-semibold'>Deed</p>
                    <button 
                        className='rounded-full p-2 hover:bg-gray-150 focus:outline-none'
                        onClick={closeModal}
                    >
                        <MdClose />
                    </button>
                </header>
                <div className='flex-grow overflow-y-scroll'>
                    <p className='mx-8 mt-4 font-medium'>The Deed, if any, required by Schedule B-I of the Commitment is identified as follows (or attach a copy)</p>
                    <div className='px-8 py-4 grid grid-cols-12 grid-flow-row gap-8'>
                        <TextArea
                            label='Mortgator(s)'
                            readOnly={readonly}
                            groupClassName='col-span-12'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.mortgators}
                            path='mortgators'
                            {...fieldActions}
                        />
                        <TextArea
                            label='Beneficiary'
                            readOnly={readonly}
                            groupClassName='col-span-12'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.beneficiary}
                            path='beneficiary'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Trustee'
                            readOnly={readonly}
                            groupClassName='col-span-12'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.trustee}
                            path='trustee'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Securing Amount Of'
                            readOnly={readonly}
                            type='currency'
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.securingAmountOf}
                            path='securingAmountOf'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Dated'
                            type='date-picker'
                            readOnly={readonly}
                            dateFormat="MM/dd/yyyy"
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            datePickerClasses="w-full"
                            value={state.values.dated}
                            path='dated'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Recorded On'
                            type='date-picker'
                            readOnly={readonly}
                            dateFormat="MM/dd/yyyy   h:mm aa"
                            showTimeInput
                            timeInputLabel="Time:"
                            datePickerClasses="w-full"
                            groupClassName='col-span-6'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.recordedOn}
                            path='recordedOn'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Book'
                            readOnly={readonly}
                            required
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.book}
                            path='book'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Page'
                            readOnly={readonly}
                            required
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.page}
                            path='page'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='County Registry'
                            readOnly={readonly}
                            required
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.countyRegistry}
                            path='countyRegistry'
                            {...fieldActions}
                        />
                        <div className='col-span-12 border-b py-2'/>
                        <FormGroup
                            label='Re-recorded On (If Applicable)'
                            type='date-picker'
                            readOnly={readonly}
                            dateFormat="MM/dd/yyyy   h:mm aa"
                            showTimeInput
                            timeInputLabel="Time:"
                            datePickerClasses="w-full"
                            groupClassName='col-span-6'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.reRecordedOn}
                            path='reRecordedOn'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Book'
                            readOnly={readonly}
                            groupClassName='col-span-3 col-start-1'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.reBook}
                            path='reBook'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Page'
                            readOnly={readonly}
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.rePage}
                            path='rePage'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='County Registry'
                            readOnly={readonly}
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.reCountyRegistry}
                            path='reCountyRegistry'
                            {...fieldActions}
                        />
                        <div className='col-span-12 border-b py-2'/>
                        <FormGroup
                            label='Assigned To'
                            readOnly={readonly}
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.assignedTo}
                            path='assignedTo'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Book'
                            readOnly={readonly}
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.assignedBook}
                            path='assignedBook'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='Page'
                            readOnly={readonly}
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.assignedPage}
                            path='assignedPage'
                            {...fieldActions}
                        />
                        <FormGroup
                            label='County Registry'
                            readOnly={readonly}
                            groupClassName='col-span-3'
                            inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                            value={state.values.assignedCountyRegistry}
                            path='assignedCountyRegistry'
                            {...fieldActions}
                        />
                        <div className='col-span-12 flex items-center'>
                            <p className='mr-4'>The Deed of Trust identified above is a valid</p>
                            <FormSelect
                                label=''
                                readOnly={readonly}
                                inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                                groupClassName='w-40'
                                value={state.values.lienOrder}
                                path='lienOrder'
                                options={state.values.lienOrderOptions}
                                {...fieldActions}
                            />
                            <p className='ml-4'>lien on the property.</p>
                        </div>
                        <FileDrop
                            files={state.values.copyOfDeed.filter(d => !d.isDeleted)}
                            className='w-full h-20'
                            groupClassName='col-span-12'
                            multiple
                            readOnly={readonly}
                            onChange={files => {
                                const _files = files
                                    .map(f => {
                                        return {
                                            ...f, 
                                            type: DocumentType.DEED_OF_TRUST,
                                            uploaded: f.uploaded == undefined ? false : f.uploaded,
                                            isDeleted: f.isDeleted != undefined ? f.isDeleted : false
                                        };
                                    });
                                fieldActions.onChange(_files, 'copyOfDeed');
                            }}
                            onRemove={(file, index) => {
                                let files = [...state.values.copyOfDeed ];
                                files.splice(index, 1);
                                fieldActions.onChange([...files, { ...file, isDeleted: true }], 'copyOfDeed');
                            }}
                            heading='Upload Copy of Deed'
                        />
                        <div className='col-span-12 flex items-center'>
                            { readonly && (
                                <button 
                                    className="ml-auto mr-2 rounded-full text-neutral-90 py-2 px-4 font-medium bg-gray-150 focus:outline-none hover:opacity-60"
                                    onClick={() => {
                                        formActions.reset();
                                        closeModal();
                                    }}
                                >
                                    Close
                                </button>
                            )}
                            { !readonly && (
                                <>
                                    <button 
                                        className="ml-auto mr-2 rounded-full text-neutral-90 py-2 px-4 font-medium bg-gray-150 focus:outline-none hover:opacity-60"
                                        onClick={() => {
                                            formActions.reset();
                                            closeModal();
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        className="rounded-full text-white bg-lightGreen-300 py-2 px-4 font-medium focus:outline-none hover:opacity-60"
                                        onClick={() => saveDeed(state.values)}
                                    >
                                        {edit ? 'Save' : 'Add' }
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </Modal>
    );
};

export default DeedOfTrustForm;